import { ConnectedPosition, Overlay, OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { UiAvatarComponent } from '../../atoms/avatar/avatar.component';
import { UiTooltipDirective } from '../../directives/tooltip/tooltip.directive';
import {
  UiNotificationMenuComponent,
  UiNotificationMenuItem,
} from '../../molecules/notification-menu/notification-menu.component';
import {
  UI_PROFILE_MENU_DEFAULT_ITEMS,
  UiProfileMenuItem,
  UiUserProfileMenuComponent,
} from '../../molecules/user-profile-menu/user-profile-menu.component';
import { UiAppHeaderVariant } from './app-header.types';

export type { UiAppHeaderVariant, UiNotificationMenuItem, UiProfileMenuItem };
export { UI_PROFILE_MENU_DEFAULT_ITEMS };

/**
 * Organismo: cabecera de aplicación (logo, notificaciones, usuario).
 * La zona de usuario usa el átomo `ui-avatar` y, si `showProfileMenu` es true,
 * la molécula `ui-user-profile-menu` en un overlay (CDK).
 * Solo presentación; sin HTTP ni Router inyectado.
 *
 * Barra de acciones: Asistente IA, Buscador, Documentos, Firma digital, Notificaciones (iconos en `public/icons/`).
 * Tooltips vía `UiTooltipDirective`; estilos globales de `.ui-tooltip` en la app.
 *
 * Las apps consumidoras deben incluir en estilos globales:
 * `@import '@angular/cdk/overlay-prebuilt.css';`
 */
@Component({
  selector: 'ui-app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OverlayModule,
    UiAvatarComponent,
    UiTooltipDirective,
    UiNotificationMenuComponent,
    UiUserProfileMenuComponent,
  ],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
  host: {
    class: 'ui-app-header-host',
    '[attr.data-variant]': 'variant()',
  },
})
export class UiAppHeaderComponent {
  private readonly overlay = inject(Overlay);

  /**
   * Variante de cabecera.
   * `system`: logo, toolbar completo y usuario (menú system).
   * `portal`: logo/nav, mensajes, notificaciones y usuario (menú portal).
   * `consultation`: logo/título, notificaciones y usuario (menú portal).
   */
  readonly variant = input<UiAppHeaderVariant>('system');

  /** Logo: URL de imagen (si no se usa proyección) */
  readonly logoSrc = input<string | null>(null);

  readonly logoAlt = input('');

  /** Enlace opcional del logo (p. ej. inicio); si falta, se puede usar logoClick o proyección */
  readonly logoHref = input<string | null>(null);

  /** Abrir enlace del logo en nueva pestaña */
  readonly logoHrefTarget = input<'_self' | '_blank'>('_self');

  /** Relación del enlace del logo (p. ej. noopener con _blank) */
  readonly logoHrefRel = input<string | null>(null);

  /** Compactar menú lateral */
  readonly showMenuToggle = input(true);
  readonly menuToggleIconSrc = input('/icons/ic_general_menu.svg');
  readonly menuToggleTooltip = input('Compactar menú');

  /** Inicio — lleva al Welcome Panel */
  readonly showHome = input(true);
  readonly homeIconSrc = input('/icons/ic_home.svg');
  readonly homeTooltip = input('Inicio');

  /** Asistente IA */
  readonly showAiAssistant = input(true);
  readonly aiAssistantIconSrc = input('/icons/ic_robot.svg');
  readonly aiAssistantTooltip = input('Asistente IA');

  /** Buscador global */
  readonly showSearch = input(true);
  readonly searchIconSrc = input('/icons/ic_search.svg');
  readonly searchTooltip = input('Buscador');

  /** Documentos */
  readonly showDocuments = input(true);
  readonly documentsIconSrc = input('/icons/ic_folder.svg');
  readonly documentsTooltip = input('Documentos');

  /** Firma digital */
  readonly showDigitalSignature = input(true);
  readonly digitalSignatureIconSrc = input('/icons/ic_signature.svg');
  readonly digitalSignatureTooltip = input('Firma digital');

  /** Ruta al icono de notificación (cada app debe servir el asset en public) */
  readonly notificationIconSrc = input('/icons/ic_notification.svg');

  readonly notificationLabel = input('Notificaciones');
  /** Texto del tooltip del botón de notificaciones */
  readonly notificationTooltip = input('Notificaciones');

  /** Muestra el botón de notificaciones */
  readonly showNotification = input(true);
  /** Badge del botón de notificaciones. Se oculta si es nulo o menor a 1. */
  readonly notificationCount = input<number | null>(null);
  /**
   * Avisos del dropdown. Sin elementos, el botón solo emite `notificationClick`.
   */
  readonly notificationItems = input<readonly UiNotificationMenuItem[]>([]);

  /**
   * Mensajes. En `portal` se muestra por defecto.
   * En las demás variantes hay que activarlo con `true`.
   */
  readonly showMessages = input<boolean | null>(null);
  readonly messagesIconSrc = input('/icons/ic_message.svg');
  readonly messagesTooltip = input('Mensajes');
  /** Badge del botón de mensajes. Se oculta si es nulo o menor a 1. */
  readonly messageCount = input<number | null>(null);

  /**
   * Muestra el slot a la izquierda de la toolbar (`[data-ui-app-header-end-start]`),
   * p. ej. un multiselect de empresas. Sin lógica de dominio: solo proyección.
   * @default false
   */
  readonly showCompanyFilter = input(false);

  readonly notificationDisabled = input(false);

  /** Indica problema con notificaciones (p. ej. error de carga) */
  readonly notificationError = input(false);

  /** Avatar: URL de imagen; si falta, usar `userAvatarLabel` / `userAvatarIconSrc` o proyección */
  readonly userAvatarSrc = input<string | null>(null);

  /** Iniciales o nombre (sin imagen ni icono) */
  readonly userAvatarLabel = input<string | null>(null);

  /** Icono SVG/PNG en `public` (sin imagen de perfil) */
  readonly userAvatarIconSrc = input<string | null>(null);

  readonly userAvatarAlt = input('Usuario');
  /** Tooltip del botón de avatar / cuenta */
  readonly userAvatarTooltip = input('Cuenta de usuario');

  /** Deshabilita la zona de usuario */
  readonly userDisabled = input(false);

  /** Estado de carga (esqueleto en avatar / deshabilita acciones) */
  readonly loading = input(false);

  /** Muestra el panel de perfil al pulsar el avatar (overlay CDK) */
  readonly showProfileMenu = input(true);

  readonly profileMenuTitle = input('Perfil de usuario');
  readonly profileUserName = input('Usuario');
  readonly profileUserRole = input('');
  readonly profileUserEmail = input('');

  /**
   * Ítems del menú de perfil. Si no se indica, usa los defaults de
   * `ui-user-profile-menu` según la variante (`system` / `portal` o `consultation`).
   */
  readonly profileMenuItems = input<readonly UiProfileMenuItem[] | undefined>(
    undefined,
  );

  readonly profileLogoutLabel = input('Cerrar sesión');

  protected readonly userMenuOpen = signal(false);
  protected readonly notificationMenuOpen = signal(false);
  protected readonly messagesVisible = computed(() => {
    const explicit = this.showMessages();
    if (explicit !== null) return explicit;
    return this.variant() === 'portal';
  });

  protected readonly profileOverlayPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
  ];

  protected readonly profileScrollStrategy = this.overlay.scrollStrategies.close();
  protected readonly notificationScrollStrategy = this.overlay.scrollStrategies.reposition();

  /** Título de navegación (alternativa al slot [data-ui-app-header-nav]) */
  readonly navTitle = input<string | null>(null);
  /** Reemplaza `navTitle` solo en móvil. */
  readonly mobileNavTitle = input<string | null>(null);

  readonly logoClick = output<void>();
  readonly menuToggleClick = output<void>();
  readonly homeClick = output<void>();
  readonly aiAssistantClick = output<void>();
  readonly searchClick = output<void>();
  readonly documentsClick = output<void>();
  readonly digitalSignatureClick = output<void>();
  readonly notificationClick = output<void>();
  readonly notificationItemSelect = output<UiNotificationMenuItem>();
  readonly notificationViewAll = output<void>();
  readonly messageClick = output<void>();
  /** Pulsar el avatar (p. ej. alternar menú o telemetría) */
  readonly userClick = output<void>();
  /** Entrada del menú de perfil */
  readonly profileMenuItemClick = output<{ id: string }>();
  readonly logoutClick = output<void>();

  @HostListener('document:keydown.escape')
  onEscapeCloseMenu(): void {
    if (this.userMenuOpen()) {
      this.closeUserMenu();
    }
    if (this.notificationMenuOpen()) {
      this.notificationMenuOpen.set(false);
    }
  }

  protected onLogoClick(event: Event): void {
    if (this.logoHref()) return;
    event.preventDefault();
    this.logoClick.emit();
  }

  protected onMenuToggleClick(): void {
    if (this.loading()) return;
    this.menuToggleClick.emit();
  }

  protected onHomeClick(): void {
    if (this.loading()) return;
    this.homeClick.emit();
  }

  protected onAiAssistantClick(): void {
    if (this.loading()) return;
    this.aiAssistantClick.emit();
  }

  protected onSearchClick(): void {
    if (this.loading()) return;
    this.searchClick.emit();
  }

  protected onDocumentsClick(): void {
    if (this.loading()) return;
    this.documentsClick.emit();
  }

  protected onDigitalSignatureClick(): void {
    if (this.loading()) return;
    this.digitalSignatureClick.emit();
  }

  protected onNotificationClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.loading() || this.notificationDisabled()) return;
    this.notificationClick.emit();
    if (!this.notificationItems().length) return;
    this.closeUserMenu();
    const next = !this.notificationMenuOpen();
    setTimeout(() => this.notificationMenuOpen.set(next));
  }

  protected onNotificationItem(item: UiNotificationMenuItem): void {
    this.notificationItemSelect.emit(item);
    this.notificationMenuOpen.set(false);
  }

  protected onNotificationViewAll(): void {
    this.notificationViewAll.emit();
    this.notificationMenuOpen.set(false);
  }

  protected onMessageClick(): void {
    if (this.loading()) return;
    this.messageClick.emit();
  }

  protected badgeLabel(count: number | null): string | null {
    if (count == null || count < 1) return null;
    return count > 99 ? '99+' : String(count);
  }

  protected toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    if (this.loading() || this.userDisabled()) return;
    this.userClick.emit();
    if (!this.showProfileMenu()) return;
    this.userMenuOpen.update((v) => !v);
  }

  protected closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  protected onBackdropClick(): void {
    this.closeUserMenu();
  }

  protected onProfileItemSelect(ev: { id: string }): void {
    this.profileMenuItemClick.emit(ev);
    this.closeUserMenu();
  }

  protected onProfileLogout(): void {
    this.logoutClick.emit();
    this.closeUserMenu();
  }
}
